import type { SearchCombinedUnitMetadata } from '@atlassian/search-client/config';

import {
	is1pProductKey,
	type PrimaryProductKey,
	type ProductKeys,
} from '../../../common/constants/product-keys';
import type { ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';
import type { QueryParams } from '../../../common/utils/query-params/types';
import { getAvailableProductsForSelectedCloudIds } from '../../../common/utils/sites';
import {
	getSelectedFilters,
	type SelectedFiltersReturnType,
} from '../../../controllers/filters/selected/get-selected-filters';
import { getSearchConfig } from '../../../ui/search-page-relay/utils/get-search-config';

export type SearchFiltersConfig = SelectedFiltersReturnType & {
	availableProducts: ProductKeys[];
	selectedProducts: ProductKeys[];
	sites: string[];
};

export type GetSearchFiltersConfigArgs = {
	/** Raw URL query parameters */
	queryParams: QueryParams;
	/** The cloud ID of the current site */
	cloudId: string;
	/** Available product keys for the current search experience */
	availableProducts: ProductKeys[];
	/** Per-site product availability from the combined search config. */
	unitMetadata?: SearchCombinedUnitMetadata[];
	/** Products excluded from the "all" product filter */
	allFilterExcludedProducts: ProductKeys[];
	/** Third-party connector configurations from bootstrap */
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	/** The current user's account ID, used for "assigned to me" / "watched by me" filters */
	currentUserId?: string | null;
	/** JSM incident filter type IDs */
	jsmIncidentFilterIds: string[];
	/** The tenant's primary product; gates the multi-site `sites` selection. */
	primaryProduct?: PrimaryProductKey;
};

export type GetSearchFiltersConfig = (args: GetSearchFiltersConfigArgs) => SearchFiltersConfig;

/**
 * Pure function that resolves search filter configuration from URL query parameters
 * and bootstrap context.
 *
 * Composes two existing pure functions:
 * 1. getSearchConfig - converts URL query params into store-shaped filters
 * 2. getSelectedFilters - converts store-shaped filters into query variable format
 *
 * This function has no React dependencies and can be called from SSR or preloading contexts.
 */
export const getSearchFiltersConfig: GetSearchFiltersConfig = ({
	queryParams,
	cloudId,
	availableProducts,
	unitMetadata,
	allFilterExcludedProducts,
	thirdPartyConfigs,
	currentUserId,
	jsmIncidentFilterIds,
	primaryProduct,
}) => {
	const {
		filters,
		selectedProducts: requestedProducts,
		selectedDatasource,
		sites,
	} = getSearchConfig({
		cloudId,
		queryParams,
		thirdPartyConfigs,
		primaryProduct,
		unitMetadata,
	});

	// Resolve app availability and explicit app selection against the same site scope.
	const productsForQuery = getAvailableProductsForSelectedCloudIds({
		currentCloudId: cloudId,
		availableProducts,
		selectedCloudIds: sites,
		unitMetadata,
	});
	const primaryIsExcluded = sites.length > 0 && !sites.includes(cloudId);
	const selectedProducts = primaryIsExcluded
		? requestedProducts.filter(is1pProductKey)
		: requestedProducts;

	const selectedFilters = getSelectedFilters({
		filters,
		selectedProducts,
		selectedDatasource,
		availableProducts: productsForQuery,
		allFilterExcludedProducts,
		thirdPartyConfigs,
		currentUserId,
		jsmIncidentFilterIds,
	});

	return {
		availableProducts: productsForQuery,
		selectedProducts,
		sites,
		...selectedFilters,
	};
};
